/** Line-profile plot with envelope, overlays, markers and sampling settings. */
import { quantityInfo } from '../../core/pipeline';
import type { LineRoi } from '../../core/roi';
import type { Interp, BandStat, OobMode } from '../../core/sampling';
import { unitScale } from '../../core/units';
import { patchRoi } from '../../app/actions';
import {
  hover,
  lineResults,
  measurement,
  pinned,
  plotHover,
  profileView,
  quantity,
  sampling,
  selectedLine,
  spatial,
  updateSignal,
  type EnvelopeMode,
} from '../../app/store';
import { PlotView, type PlotHandle } from '../../plot/PlotView';
import type { Band, PlotSpec, Series, Span, WidthMark, PointMark, HLine } from '../../plot/scene';
import { Button, NumberInput, Select, Toggle } from '../controls';

export const profilePlotHandle: { current: PlotHandle | null } = { current: null };

/** Build the plot spec for the current selection (shared with export). */
export function buildProfileSpec(): PlotSpec {
  const sel = selectedLine.value;
  const results = lineResults.value;
  const pv = profileView.value;
  const m = measurement.value;
  const sp = spatial.value;
  const k = unitScale(sp);
  const xLabel = `Distance along line [${sp.unit}]`;
  const yLabel = m?.axis ?? quantityInfo(quantity.value).short;
  const series: Series[] = [];
  const bands: Band[] = [];
  const spans: Span[] = [];
  const widths: WidthMark[] = [];
  const points: PointMark[] = [];
  const hlines: HLine[] = [];
  const scaleX = (t: Float64Array) => (k === 1 ? t : t.map((v) => v * k));
  const norm = (y: Float64Array) => {
    if (!pv.normalize) return y;
    let mx = -Infinity;
    for (const v of y) if (Number.isFinite(v) && v > mx) mx = v;
    return mx > 0 ? y.map((v) => v / mx) : y;
  };
  const lines = pv.showAllLines ? [...results.values()] : sel && results.get(sel.id) ? [results.get(sel.id)!] : [];
  // Selected line last so it draws on top.
  lines.sort((a, b) => (a.roi.id === sel?.id ? 1 : 0) - (b.roi.id === sel?.id ? 1 : 0));
  for (const r of lines) {
    const x = scaleX(r.profile.t);
    const isSel = r.roi.id === sel?.id;
    if (isSel && pv.envelope !== 'none' && !pv.normalize) {
      // For a band *sum* the envelope is scaled to the sum: n·min … n·max, ±√n·σ.
      const sum = sampling.value.stat === 'sum';
      const pr = r.profile;
      const kSd = (i: number) => (sum ? Math.sqrt(pr.cnt[i]) : 1) * pr.sd[i];
      const kMm = (i: number) => (sum ? pr.cnt[i] : 1);
      const lo = pv.envelope === 'sd' ? pr.v.map((v, i) => v - kSd(i)) : pr.lo.map((v, i) => v * kMm(i));
      const hi = pv.envelope === 'sd' ? pr.v.map((v, i) => v + kSd(i)) : pr.hi.map((v, i) => v * kMm(i));
      if (r.roi.width > 1) bands.push({ x, lo, hi, color: hexA(r.roi.color, 0.22) });
    }
    series.push({ id: r.roi.id, label: r.roi.name, x, y: norm(r.profile.v), color: r.roi.color, width: isSel ? 2 : 1.25 });
    if (isSel && pv.showB && r.profileB)
      series.push({
        id: `${r.roi.id}-B`,
        label: `${r.roi.name} · image B`,
        x,
        y: norm(r.profileB.v),
        color: '#9aa4b2',
        width: 1.25,
        dash: [5, 3],
      });
    if (isSel && pv.showDerivative && !pv.normalize)
      series.push({
        id: `${r.roi.id}-d`,
        label: `d/dx`,
        x,
        y: r.derivative.map((v) => v / k),
        color: '#8fd694',
        width: 1,
        dash: [2, 2],
        axis: 'y2',
      });
    if (isSel) {
      // Shade clipped samples.
      let start = -1;
      for (let i = 0; i <= r.profile.n; i++) {
        const c = i < r.profile.n && r.profile.clip[i] & 1;
        if (c && start < 0) start = i;
        if (!c && start >= 0) {
          // Extend each clipped run by half a sample on both sides so single samples stay visible.
          const x0 = start > 0 ? (x[start - 1] + x[start]) / 2 : x[start];
          const x1 = i < r.profile.n ? (x[i - 1] + x[i]) / 2 : x[r.profile.n - 1];
          spans.push({ x0, x1, color: 'rgba(255,60,60,0.18)' });
          start = -1;
        }
      }
      if (pv.showMarkers && !pv.normalize) {
        const met = r.metrics;
        const b = met.baseline;
        if (b.mode !== 'zero' && !b.slope) hlines.push({ y: b.a, color: '#7f8a99', label: `baseline (${b.mode})` });
        met.peaks.forEach((p, i) => {
          points.push({ x: p.x * k, y: p.value, color: '#ffffff', label: met.peaks.length > 1 ? `${i + 1}` : undefined });
          // Mark the 50 % width if it is one of the levels; otherwise the first level, labelled as such.
          const fw = p.widths.find((w) => Math.abs(w.level - 0.5) < 1e-9) ?? p.widths[0];
          if (fw && fw.left !== null && fw.right !== null) {
            const name = Math.abs(fw.level - 0.5) < 1e-9 ? 'FWHM' : `FW ${Number((fw.level * 100).toPrecision(3))} %`;
            widths.push({ x0: fw.left * k, x1: fw.right * k, y: fw.value, color: '#ffd166', label: `${name} ${fmtShort(fw.width! * k)}` });
          }
        });
      }
    }
  }
  if (pv.showPinned)
    for (const p of pinned.value)
      series.push({ id: p.id, label: p.label, x: scaleX(p.x), y: norm(p.y), color: p.color, width: 1.25, dash: [6, 3] });
  const hasDeriv = series.some((s) => s.axis === 'y2');
  return {
    series,
    bands,
    spans,
    widths,
    points,
    hlines,
    xLabel,
    yLabel: pv.normalize ? `${yLabel} (normalised)` : yLabel,
    y2Label: hasDeriv ? `d/dx [${m?.unit || '1'}/${sp.unit}]` : undefined,
    yLog: pv.yLog,
    yRange: pv.yMin !== null && pv.yMax !== null && pv.yMax > pv.yMin ? [pv.yMin, pv.yMax] : null,
    legend: series.length > 1,
  };
}

function fmtShort(v: number) {
  return Number(v.toPrecision(4)).toString();
}

function hexA(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Project the image cursor onto the selected line (distance in px), if it is close to it. */
function cursorOnLine(l: LineRoi | null): number | null {
  const h = hover.value;
  if (!h || !l) return null;
  const L = Math.hypot(l.x1 - l.x0, l.y1 - l.y0);
  if (!L) return null;
  const ux = (l.x1 - l.x0) / L;
  const uy = (l.y1 - l.y0) / L;
  const t = (h.x - l.x0) * ux + (h.y - l.y0) * uy;
  const d = Math.abs(-(h.x - l.x0) * uy + (h.y - l.y0) * ux);
  if (t < 0 || t > L || d > Math.max(l.width, 6)) return null;
  return t;
}

export function ProfilePanel() {
  const sel = selectedLine.value;
  const s = sampling.value;
  const pv = profileView.value;
  const k = unitScale(spatial.value);
  const spec = buildProfileSpec();
  const t = cursorOnLine(sel);
  const key = `${sel?.id}|${spatial.value.unit}|${quantity.value}`;
  return (
    <div class="profile-panel">
      <div class="toolbar-row">
        <span class="row-label" data-help="profile.line">
          {sel ? <b style={{ color: sel.color }}>{sel.name}</b> : 'No line'}
        </span>
        <span class="field-inline" data-help="sampling.width">
          width
          <NumberInput
            value={sel?.width ?? 1}
            integer
            min={1}
            max={999}
            disabled={!sel}
            testId="line-width"
            unit="px"
            onChange={(v) => sel && patchRoi(sel.id, { width: v } as Partial<LineRoi>)}
          />
        </span>
        <span class="field-inline" data-help="sampling.interp">
          <Select<Interp>
            value={s.interp}
            testId="interp"
            options={[
              { value: 'nearest', label: 'Nearest' },
              { value: 'bilinear', label: 'Bilinear' },
              { value: 'bicubic', label: 'Bicubic' },
            ]}
            onChange={(v) => updateSignal(sampling, { interp: v })}
          />
        </span>
        <span class="field-inline" data-help="sampling.step">
          step
          <NumberInput value={s.step} min={0.05} max={50} step={0.1} unit="px" onChange={(v) => updateSignal(sampling, { step: v })} />
        </span>
        <span class="field-inline" data-help="sampling.stat">
          <Select<BandStat>
            value={s.stat}
            options={[
              { value: 'mean', label: 'Band mean' },
              { value: 'median', label: 'Band median' },
              { value: 'min', label: 'Band min' },
              { value: 'max', label: 'Band max' },
              { value: 'sum', label: 'Band sum' },
            ]}
            onChange={(v) => updateSignal(sampling, { stat: v })}
          />
        </span>
        <span class="field-inline" data-help="sampling.oob">
          <Select<OobMode>
            value={s.oob}
            options={[
              { value: 'nan', label: 'Outside → gap' },
              { value: 'clamp', label: 'Outside → clamp' },
            ]}
            onChange={(v) => updateSignal(sampling, { oob: v })}
          />
        </span>
        <span class="field-inline" data-help="profile.envelope">
          <Select<EnvelopeMode>
            value={pv.envelope}
            options={[
              { value: 'sd', label: '±σ envelope' },
              { value: 'minmax', label: 'min–max envelope' },
              { value: 'none', label: 'No envelope' },
            ]}
            onChange={(v) => updateSignal(profileView, { envelope: v })}
          />
        </span>
      </div>
      <div class="toolbar-row secondary">
        <Toggle
          label="All lines"
          checked={pv.showAllLines}
          help="profile.allLines"
          onChange={(v) => updateSignal(profileView, { showAllLines: v })}
        />
        <Toggle label="Image B" checked={pv.showB} help="profile.showB" onChange={(v) => updateSignal(profileView, { showB: v })} />
        <Toggle
          label="Markers"
          checked={pv.showMarkers}
          help="profile.markers"
          onChange={(v) => updateSignal(profileView, { showMarkers: v })}
        />
        <Toggle
          label="Derivative"
          checked={pv.showDerivative}
          help="profile.derivative"
          onChange={(v) => updateSignal(profileView, { showDerivative: v })}
        />
        <Toggle
          label="Normalise"
          checked={pv.normalize}
          help="profile.normalize"
          onChange={(v) => updateSignal(profileView, { normalize: v })}
        />
        <Toggle label="Log y" checked={pv.yLog} help="profile.ylog" onChange={(v) => updateSignal(profileView, { yLog: v })} />
        <span class="field-inline" data-help="profile.yrange">
          y
          <NumberInput value={pv.yMin} placeholder="auto" onChange={(v) => updateSignal(profileView, { yMin: v })} />
          –
          <NumberInput value={pv.yMax} placeholder="auto" onChange={(v) => updateSignal(profileView, { yMax: v })} />
          {(pv.yMin !== null || pv.yMax !== null) && (
            <Button small kind="ghost" onClick={() => updateSignal(profileView, { yMin: null, yMax: null })}>
              auto
            </Button>
          )}
        </span>
        <span class="spacer" />
        <Button small icon="pin" help="profile.pin" disabled={!sel} onClick={pinCurrent} testId="pin-profile">
          Pin
        </Button>
        {pinned.value.length > 0 && (
          <Button small kind="ghost" help="profile.unpin" onClick={() => (pinned.value = [])}>
            Clear pins
          </Button>
        )}
      </div>
      <PlotView
        spec={spec}
        resetKey={key}
        handle={profilePlotHandle}
        testId="profile-plot"
        hoverX={t !== null ? t * k : null}
        emptyText={measurement.value ? 'Draw a line on the image (L) to see its profile' : 'Load an image to begin'}
        onHover={(x) => {
          const l = selectedLine.value;
          plotHover.value = x !== null && l ? { roiId: l.id, t: x / k } : null;
        }}
      />
    </div>
  );
}

function pinCurrent() {
  const sel = selectedLine.value;
  const r = sel ? lineResults.value.get(sel.id) : null;
  if (!sel || !r) return;
  const colors = ['#f4a261', '#e76f51', '#2a9d8f', '#e9c46a', '#a8dadc', '#ff99c8'];
  const n = pinned.value.length;
  pinned.value = [
    ...pinned.value,
    {
      id: `pin-${Date.now()}`,
      label: `${sel.name} (pinned ${new Date().toLocaleTimeString()})`,
      color: colors[n % colors.length],
      x: r.profile.t.slice(),
      y: r.profile.v.slice(),
      unit: measurement.value?.unit ?? '',
    },
  ];
}
